import { useEffect, useRef, useState } from "react"
import { useMenu, UseMenuProps } from "react-instantsearch"

type MenuSelectProps = UseMenuProps & {
  name: string
  labels?: Record<string, string>
}

export default function MenuSelect({
  name,
  labels,
  ...props
}: MenuSelectProps) {
  const { items, refine } = useMenu(props)
  const { value: selectedValue } = items.find(item => item.isRefined) || {
    value: "",
  }
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const toggleMenu = () => setMenuOpen(prev => !prev)

  const selectItem = (value: string) => {
    refine(value)
    setMenuOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  const selectedLabel = selectedValue
    ? `${labels ? labels[selectedValue] : selectedValue}`
    : name

  return (
    <div className="tablet:relative" ref={containerRef}>
      <button
        type="button"
        data-test={`filter-${name}`}
        onClick={toggleMenu}
        className={`dropdown-button disabled:opacity-50 disabled:cursor-not-allowed ${menuOpen ? "dropdown-button--active" : ""}`}
        aria-expanded={menuOpen}
        aria-haspopup="listbox"
        disabled={items.length === 0}
      >
        {selectedLabel}
        <svg
          className={`icon w-16 h-16 transition-transform ${menuOpen ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div
        className={`dropdown-menu-container ${menuOpen ? "dropdown-menu-container--active" : ""}`}
        role="listbox"
        data-test={`filter-options-${name}`}
        aria-label={name}
      >
        {items
          .filter(
            ({ value }) =>
              value !== "" &&
              (props.attribute !== "themes" ||
                !labels ||
                labels[value] !== "General")
          )
          .map(item => (
            <div className="dropdown-item" key={item.value}>
              <button
                type="button"
                role="option"
                aria-selected={item.isRefined}
                onClick={() => selectItem(item.value)}
              >
                {labels ? labels[item.value] : item.value}{" "}
                <span className="text-10 opacity-70 align-top">
                  ({item.count})
                </span>
              </button>
            </div>
          ))}
      </div>
    </div>
  )
}
